import React, { useEffect, useRef, useState } from 'react'
import { HtmlContent, Grid, Tabs, Tab, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { PresentationBlockProps } from '../..'
import compareBlockProps from '../../util/compareBlockProps'
import CodeIcon from '@material-ui/icons/Code'
import { CodeEditor } from '../../components'
import { useDebouncedCommit } from '../../util/useDebouncedCommit'
import { sanitizeCustomCss } from './sanitizeCustomCss'

// HtmlContent's sanitizer (striptags) only strips disallowed tags — it does not strip attributes
// off allowed ones. So this list must exclude any tag that can execute script, navigate, submit,
// or embed external content (form, iframe, input/button with event-handler attributes), since
// this content is rendered to customers in a public presentation. Keep it to inert formatting tags.
const additionalTags = ['pre', 'blockquote', 'code']

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: { position: 'relative' },
    content: {},
    tabs: {
      minHeight: 0,
      marginBottom: theme.spacing(1)
    },
    tab: {
      minHeight: 0,
      minWidth: 0,
      padding: theme.spacing(0.5, 2),
      textTransform: 'none',
      letterSpacing: 0
    },
    codeEditor: {
      fontSize: '0.875rem'
    },
    codeIcon: {
      position: 'absolute',
      bottom: 8,
      right: 8,
      opacity: 0.5,
      pointerEvents: 'none'
    }
  }
})

function CustomBlock(props: PresentationBlockProps) {
  const classes = useStyles(props)
  const { isEditable, isPreview, isSettings, block, blockPrefix, setFieldValue, submitForm, WrapperComponent } = props
  const { fields } = block
  const [tab, setTab] = useState<'html' | 'css'>('html')
  const [html, setHtml] = useState(fields?.html ?? '')
  const [css, setCss] = useState(fields?.css ?? '')

  // The debounced commit below round-trips whatever was just typed back down through Formik into
  // `fields.html`/`fields.css` a moment later — these refs let the sync effects tell that echo apart
  // from a real external change (undo, this block getting reused elsewhere on reorder). Only a value
  // this block did NOT itself just commit should reset the local editor state (#23827). This relies
  // on the round-tripped string coming back byte-identical to what we wrote — Formik doesn't touch
  // it, so that holds today. It would break if something started re-serialising the HTML/CSS in
  // between (a sanitiser, a GraphQL round trip that doesn't preserve the string exactly) — the
  // symptom would be this bug's exact return: the field blurring again ~400ms into typing.
  const lastCommittedHtmlRef = useRef(fields?.html ?? '')
  const lastCommittedCssRef = useRef(fields?.css ?? '')

  useEffect(() => {
    if ((fields?.html ?? '') === lastCommittedHtmlRef.current) return
    setHtml(fields?.html ?? '')
  }, [fields?.html])

  useEffect(() => {
    if ((fields?.css ?? '') === lastCommittedCssRef.current) return
    setCss(fields?.css ?? '')
  }, [fields?.css])

  // `lastCommittedRef` is passed in by the caller rather than resolved from `field` here — keying
  // off the field name (e.g. `field === 'fields.html'`) would silently point at the wrong ref if a
  // caller ever passed the bare field name instead of the full `fields.html`/`fields.css` path.
  const handleEditField = (field: string, content: string, lastCommittedRef: React.MutableRefObject<string>) => {
    lastCommittedRef.current = content
    setFieldValue(`${blockPrefix}.${field}`, content)
    submitForm()
  }

  // Typing commits on a debounce as well, so the form is dirty (and Save is clickable) while a code
  // editor still has focus — see useDebouncedCommit for why. The two tabs share one timer because
  // only one is ever mounted at a time; switching tabs blurs the editor, which commits it.
  const draftCommit = useDebouncedCommit(
    ({
      field,
      content,
      lastCommittedRef
    }: {
      field: string
      content: string
      lastCommittedRef: React.MutableRefObject<string>
    }) => handleEditField(field, content, lastCommittedRef)
  )

  const handleCodeChange = (
    field: string,
    content: string,
    setLocal: (value: string) => void,
    lastCommittedRef: React.MutableRefObject<string>
  ) => {
    setLocal(content)
    draftCommit.schedule({ field, content, lastCommittedRef })
  }

  const handleCodeBlur = (field: string, content: string, lastCommittedRef: React.MutableRefObject<string>) => {
    // Drop any scheduled commit first — it holds the same content this is about to write.
    draftCommit.cancel()
    handleEditField(field, content, lastCommittedRef)
  }

  return (
    <WrapperComponent {...props}>
      <Grid container spacing={3} classes={{ root: classes.root }}>
        {isSettings && !isPreview && !isEditable && <CodeIcon className={classes.codeIcon} />}
        <Grid item md={12}>
          {isEditable ? (
            <>
              <Tabs
                className={classes.tabs}
                value={tab}
                onChange={(_ev, newTab) => setTab(newTab)}
                indicatorColor={'primary'}
              >
                <Tab className={classes.tab} value={'html'} label={'HTML'} />
                <Tab className={classes.tab} value={'css'} label={'CSS'} />
              </Tabs>
              {tab === 'html' && (
                <CodeEditor
                  language={'html'}
                  placeholder={'Add HTML'}
                  value={html}
                  onChange={(value) => handleCodeChange('fields.html', value, setHtml, lastCommittedHtmlRef)}
                  onBlur={() => handleCodeBlur('fields.html', html, lastCommittedHtmlRef)}
                  className={classes.codeEditor}
                />
              )}
              {tab === 'css' && (
                <CodeEditor
                  language={'css'}
                  placeholder={'Add CSS'}
                  value={css}
                  onChange={(value) => handleCodeChange('fields.css', value, setCss, lastCommittedCssRef)}
                  onBlur={() => handleCodeBlur('fields.css', css, lastCommittedCssRef)}
                  className={classes.codeEditor}
                />
              )}
            </>
          ) : (
            <HtmlContent
              includeTags={additionalTags}
              content={fields?.html ?? ''}
              classes={{ root: classes.content }}
            />
          )}
          <HtmlContent includeTags={['style']} content={`<style>${sanitizeCustomCss(fields?.css ?? '')}</style>`} />
        </Grid>
      </Grid>
    </WrapperComponent>
  )
}

export default React.memo(CustomBlock, (prevProps, nextProps) => {
  return compareBlockProps(prevProps, nextProps)
})
